import {
	OrderInfo,
	SaleDatapoint,
	Weekday,
	salesChartMode,
	DashboardDatapoint,
	OrderProduct,
} from 'common/types';
import moment from 'moment-timezone';
import { ChartData } from 'react-chartjs-2';
import * as _ from 'lodash';
import * as chartjs from 'chart.js';
import * as Colors from 'common/styles/colors';
import i18n from 'services/localization/i18n';

export const mergeDatapointRevenues = (
	first: SaleDatapoint,
	second: SaleDatapoint,
): SaleDatapoint => {
	const result = { ...first };
	result.revenue += second.revenue;
	return result;
};

export const mergeDatapointRentals = (
	first: DashboardDatapoint,
	second: DashboardDatapoint,
): DashboardDatapoint => {
	const result = { ...first };
	result.productAmount += second.productAmount;
	return result;
};

export const dayLabelFromSalesEntry = (sale: SaleDatapoint | DashboardDatapoint): string => {
	const date = moment(sale.timestamp);
	const dayNumber = date.format('D');
	const weekday = date.format('ddd');
	return `${weekday} ${dayNumber}`;
};

export const hourLabelFromSalesEntry = (sale: SaleDatapoint | DashboardDatapoint): string => {
	const date = moment(sale.timestamp).hour(sale.hour);
	return date.format('HH');
};

export const chartDataFromSales = (
	sales: SaleDatapoint[],
	viewMode: salesChartMode,
	day: moment.Moment | null,
): ChartData<chartjs.ChartData> => {
	let labelSet: string[] = [];
	let dataset: number[] = [];
	if (viewMode === 'day') {
		if (!day) {
			throw new Error('Must provide day number if chart is showing hourly sales breakdown');
		}
		labelSet = sales.map((sale) => {
			return hourLabelFromSalesEntry(sale);
		});
	} else {
		labelSet = sales.map((sale) => {
			return dayLabelFromSalesEntry(sale);
		});
	}
	dataset = sales.map((sale) => {
		return sale.revenue / 100;
	});
	return {
		labels: labelSet,
		datasets: [
			{
				data: dataset,
				backgroundColor: Colors.rentleGreen.main,
			},
		],
	};
};

export const chartDataFromRentals = (
	rentalsOut: DashboardDatapoint[],
	rentalsIn: DashboardDatapoint[],
	viewMode: salesChartMode,
	day: moment.Moment | null,
) => {
	let labelSet: string[] = [];
	let dataset: number[] = [];
	let dataset2: number[] = [];
	if (viewMode === 'day') {
		if (!day) {
			throw new Error('Must provide day number if chart is showing hourly rental breakdown');
		}
		labelSet = rentalsOut.map((rental) => {
			return hourLabelFromSalesEntry(rental);
		});
	} else if (viewMode === 'week') {
		if (!day) {
			throw new Error('Must provide day number if chart is showing weekly rental breakdown');
		}
		labelSet = rentalsOut.map((rental) => {
			return dayLabelFromSalesEntry(rental);
		});
	} else {
		labelSet = rentalsOut.map((rental) => {
			return dayLabelFromSalesEntry(rental);
		});
	}

	dataset = rentalsOut.map((rentalOut) => {
		return rentalOut.productAmount;
	});
	dataset2 = rentalsIn.map((rentalIn) => {
		return rentalIn.productAmount;
	});
	return {
		labels: labelSet,
		datasets: [
			{
				label: i18n.t('dashboard.outgoing'),
				data: dataset,
				backgroundColor: Colors.rentleGreen.main,
			},
			{
				label: i18n.t('dashboard.incoming'),
				data: dataset2,
				backgroundColor: Colors.peach.main,
			},
		],
	};
};

const fillMissingSalesDays = (
	salesData: _.Dictionary<SaleDatapoint[]>,
): _.Dictionary<SaleDatapoint[]> => {
	const dailyData: _.Dictionary<SaleDatapoint[]> = _.clone(salesData);
	const saleDays = Object.keys(dailyData).map(Number);
	const sampleDate = saleDays.length > 0 ? moment(dailyData[saleDays[0]][0].timestamp) : moment();
	const daysInMonth = Number(sampleDate.endOf('month').format('D'));
	const allDays = Array.from(Array(daysInMonth).keys()).map((e) => e + 1);
	allDays
		.filter((day) => saleDays.indexOf(day) < 0) // Drop days that have sales
		.forEach((day) => {
			const currentDay = sampleDate.date(day);
			const zeroSaleDay: SaleDatapoint = {
				timestamp: currentDay.toISOString(),
				dayNumber: Number(currentDay.format('D')),
				hour: Number(currentDay.format('H')),
				weekday: currentDay.format('dddd') as Weekday,
				revenue: 0,
			};
			_.set(dailyData, day, [zeroSaleDay]);
		});
	return dailyData;
};

const fillMissingRentalDays = (
	rentalData: _.Dictionary<DashboardDatapoint[]>,
	startDay: moment.Moment,
): _.Dictionary<DashboardDatapoint[]> => {
	const dailyData: _.Dictionary<DashboardDatapoint[]> = _.clone(rentalData);
	const rentalDays = Object.keys(dailyData).map(Number);
	const sampleDate =
		rentalDays.length > 0 ? moment(dailyData[rentalDays[0]][0].timestamp) : moment(startDay);
	const daysInSelectedView = Number(sampleDate.endOf('month').format('D'));
	const allDays = Array.from(Array(daysInSelectedView).keys()).map((e) => e + 1);
	allDays
		.filter((day) => rentalDays.indexOf(day) < 0) // Drop days that have sales
		.forEach((day) => {
			const currentDay = sampleDate.date(day);
			const cominedDayAndMonth = currentDay.format('MMDD');
			const zeroRentalDay: DashboardDatapoint = {
				timestamp: currentDay.toISOString(),
				dayNumber: Number(currentDay.format('D')),
				hour: Number(currentDay.format('H')),
				weekday: currentDay.format('dddd') as Weekday,
				revenue: 0,
				productAmount: 0,
				cominedDayAndMonth,
			};
			_.set(dailyData, day, [zeroRentalDay]);
		});
	return dailyData;
};

const fillMissingRentalDaysWeek = (
	rentalData: _.Dictionary<DashboardDatapoint[]>,
	startDay: moment.Moment,
): _.Dictionary<DashboardDatapoint[]> => {
	const dailyData: _.Dictionary<DashboardDatapoint[]> = _.clone(rentalData);
	const rentalDays = Object.keys(dailyData).map(Number);
	const sampleDate =
		rentalDays.length > 0
			? moment(dailyData[rentalDays[0]][0].timestamp).startOf('isoWeek')
			: moment(startDay).startOf('isoWeek');
	const sampleDateDate = parseInt(sampleDate.format('D'));
	const daysInSelectedView = 7;
	const daysInMonth = Number(sampleDate.endOf('month').format('D'));
	const allDays = [];
	let newMonth = 0;
	for (let i = 0; i < daysInSelectedView; i++) {
		const newNumber =
			sampleDateDate + i <= daysInMonth ? sampleDateDate + i : sampleDateDate + i - daysInMonth;
		allDays.push(newNumber);
		if (sampleDateDate + i > daysInMonth) {
			newMonth = sampleDate.month() + 1;
		}
	}
	allDays
		.filter((day) => rentalDays.indexOf(day) < 0) // Drop days that have sales
		.forEach((day) => {
			const currentDay =
				day < 6 && newMonth > 0 ? sampleDate.date(day).month(newMonth) : sampleDate.date(day);
			const cominedDayAndMonth = currentDay.format('MMDD');
			const zeroRentalDay: DashboardDatapoint = {
				timestamp: currentDay.toISOString(),
				dayNumber: Number(currentDay.format('D')),
				hour: Number(currentDay.format('H')),
				weekday: currentDay.format('dddd') as Weekday,
				revenue: 0,
				productAmount: 0,
				cominedDayAndMonth,
			};
			_.set(dailyData, day, [zeroRentalDay]);
		});
	return dailyData;
};

const fillMissingSalesHours = (
	sales: _.Dictionary<SaleDatapoint[]>,
	day: moment.Moment,
): _.Dictionary<SaleDatapoint[]> => {
	const hourlyData = _.clone(sales);
	const salesHours = Object.keys(hourlyData).map(Number);
	const allHours = Array.from(Array(24).keys());
	allHours
		.filter((hour) => salesHours.indexOf(hour) < 0)
		.forEach((hour) => {
			const emptySale: SaleDatapoint = {
				timestamp: day.toISOString(),
				dayNumber: Number(day.format('D')),
				hour,
				weekday: day.format('dddd') as Weekday,
				revenue: 0,
			};
			_.set(hourlyData, hour, [emptySale]);
		});
	return hourlyData;
};

const fillMissingRentalHours = (
	rentals: _.Dictionary<DashboardDatapoint[]>,
	day: moment.Moment,
): _.Dictionary<DashboardDatapoint[]> => {
	const hourlyData = _.clone(rentals);
	const rentalHours = Object.keys(hourlyData).map(Number);
	const allHours = Array.from(Array(24).keys());
	allHours
		.filter((hour) => rentalHours.indexOf(hour) < 0)
		.forEach((hour) => {
			const emptyRental: DashboardDatapoint = {
				timestamp: day.toISOString(),
				dayNumber: Number(day.format('D')),
				hour,
				weekday: day.format('dddd') as Weekday,
				revenue: 0,
				productAmount: 0,
				cominedDayAndMonth: day.format('MMDD'),
			};
			_.set(hourlyData, hour, [emptyRental]);
		});
	return hourlyData;
};

export const saleDatapointFromRental = (rental: OrderInfo): SaleDatapoint => {
	const date = moment(rental.startDate || '');
	const salesData: SaleDatapoint = {
		timestamp: rental.startDate || '',
		dayNumber: Number(date.format('D')),
		hour: Number(date.format('H')),
		weekday: date.format('dddd') as Weekday,
		revenue: rental.pricing.total,
	};
	return salesData;
};

export const rentalDatapointFromProduct = (
	product: OrderProduct,
	direction: string,
): DashboardDatapoint => {
	const date =
		direction === 'out'
			? moment(product.startDate || '')
			: product.endDateReturned
			? moment(product.endDateReturned) || ''
			: moment(product.endDate || '');
	const dashboardData: DashboardDatapoint = {
		timestamp:
			direction === 'out'
				? product.startDate || ''
				: product.endDateReturned
				? product.endDateReturned || ''
				: product.endDate || '',
		dayNumber: Number(date.format('D')),
		hour: Number(date.format('H')),
		weekday: date.format('dddd') as Weekday,
		revenue: product.pricing.total,
		productAmount: 1,
		cominedDayAndMonth: date.format('MMDD'),
	};
	return dashboardData;
};

const combineOneDaySales = (
	allSales: _.Dictionary<SaleDatapoint[]>,
	currentDate: moment.Moment,
): SaleDatapoint[] => {
	const dailyData: SaleDatapoint[] = [];
	Object.keys(allSales).forEach((key) => {
		const currentDay = currentDate.date(Number(key));
		const emptySale: SaleDatapoint = {
			timestamp: currentDay.toISOString(),
			dayNumber: Number(currentDay.format('D')),
			hour: Number(currentDay.format('H')),
			weekday: currentDay.format('dddd') as Weekday,
			revenue: 0,
		};
		const oneDayTotal = allSales[key].reduce(mergeDatapointRevenues, emptySale);
		dailyData.push(oneDayTotal);
	});
	return dailyData;
};

const combineOneDayRentals = (
	allRentals: _.Dictionary<DashboardDatapoint[]>,
	currentDate: moment.Moment,
	viewModeIsWeek: boolean,
): DashboardDatapoint[] => {
	const dailyData: DashboardDatapoint[] = [];
	const lastKey = Object.keys(allRentals).slice(-1).pop();
	Object.keys(allRentals).forEach((key) => {
		const currentDay = viewModeIsWeek
			? Number(key) < 6 && Number(lastKey) > 20
				? moment(currentDate).date(Number(key)).add(1, 'month')
				: currentDate.date(Number(key))
			: currentDate.date(Number(key));
		const emptySale: DashboardDatapoint = {
			timestamp: currentDay.toISOString(),
			dayNumber: Number(currentDay.format('D')),
			hour: Number(currentDay.format('H')),
			weekday: currentDay.format('dddd') as Weekday,
			revenue: 0,
			productAmount: 0,
			cominedDayAndMonth: currentDay.format('MMDD'),
		};
		const oneDayTotal: DashboardDatapoint = allRentals[key].reduce(
			mergeDatapointRentals,
			emptySale,
		);
		dailyData.push(oneDayTotal);
	});
	return dailyData;
};

const combineHourlySales = (
	allSales: _.Dictionary<SaleDatapoint[]>,
	day: moment.Moment,
): SaleDatapoint[] => {
	const hourlyData: SaleDatapoint[] = [];
	Object.keys(allSales).forEach((key) => {
		const hour = Number(key);
		const emptySaleTemplate: SaleDatapoint = {
			timestamp: day.toISOString(),
			dayNumber: Number(day.format('D')),
			hour,
			weekday: day.format('dddd') as Weekday,
			revenue: 0,
		};
		const oneHourTotal = allSales[key].reduce(mergeDatapointRevenues, emptySaleTemplate);
		hourlyData.push(oneHourTotal);
	});
	return hourlyData;
};

const combineHourlyRentals = (
	allRentals: _.Dictionary<DashboardDatapoint[]>,
	day: moment.Moment,
): DashboardDatapoint[] => {
	const hourlyData: DashboardDatapoint[] = [];
	Object.keys(allRentals).forEach((key) => {
		const hour = Number(key);
		const emptyRentalTemplate: DashboardDatapoint = {
			timestamp: day.toISOString(),
			dayNumber: Number(day.format('D')),
			hour,
			weekday: day.format('dddd') as Weekday,
			revenue: 0,
			productAmount: 0,
		};
		const oneHourTotal = allRentals[key].reduce(mergeDatapointRentals, emptyRentalTemplate);
		hourlyData.push(oneHourTotal);
	});
	return hourlyData;
};

export const salesDataFromRentals = (
	rentals: OrderInfo[],
	viewMode: salesChartMode,
	day: moment.Moment | null,
): SaleDatapoint[] => {
	const salesDataDump = rentals.map(saleDatapointFromRental);
	const sampleDate = salesDataDump.length > 0 ? moment(salesDataDump[0].timestamp) : moment();
	const salesByDate = _.groupBy(salesDataDump, 'dayNumber');
	const allSales = fillMissingSalesDays(salesByDate);
	if (viewMode === 'month') {
		return combineOneDaySales(allSales, sampleDate);
	} else {
		if (day === null) {
			throw new Error('Day number was not provided for fetching hourly breakdown of sales.');
		}
		const salesForChosenDay = allSales[day.format('D')];
		const salesByHour = _.groupBy(salesForChosenDay, 'hour');
		const allSalesForChosenDay = fillMissingSalesHours(salesByHour, day);
		return combineHourlySales(allSalesForChosenDay, day);
	}
};

export const rentalDataFromOrders = (
	products: OrderProduct[],
	viewMode: string,
	day: moment.Moment,
	direction: string,
): DashboardDatapoint[] => {
	const viewModeIsWeek = viewMode === 'week';
	const rentalDataDump = products.map((products) =>
		rentalDatapointFromProduct(products, direction),
	);
	const sampleDate = rentalDataDump.length > 0 ? moment(rentalDataDump[0].timestamp) : moment(day);
	const rentalsByDate = _.groupBy(rentalDataDump, 'dayNumber');
	const allRentals = viewModeIsWeek
		? fillMissingRentalDaysWeek(rentalsByDate, sampleDate)
		: fillMissingRentalDays(rentalsByDate, sampleDate);
	if (viewMode === 'week') {
		const rentalsOut = combineOneDayRentals(allRentals, sampleDate, viewModeIsWeek);
		if (
			rentalsOut.some((rental) => rental.dayNumber < 6) &&
			rentalsOut.some((rental) => rental.dayNumber > 20)
		) {
			const newArray = rentalsOut.filter((rental) => rental.dayNumber < 6);
			const newArray2 = rentalsOut.filter((rental) => rental.dayNumber > 20);
			const fixedCount = newArray2.concat(newArray);
			return fixedCount;
		}
		return rentalsOut;
	} else if (viewMode === 'month') {
		return combineOneDayRentals(allRentals, sampleDate, viewModeIsWeek);
	} else {
		if (day === null) {
			throw new Error('Day number was not provided for fetching hourly breakdown of sales.');
		}
		const rentalsForChosenDay = allRentals[day.format('D')];
		const rentlasByHour = _.groupBy(rentalsForChosenDay, 'hour');
		const allRentalsForChosenDay = fillMissingRentalHours(rentlasByHour, day);
		return combineHourlyRentals(allRentalsForChosenDay, day);
	}
};
